import React, { FC, useEffect, useState, useCallback } from 'react';
import { default as isMobileUATest } from 'ismobilejs';
import throttle from 'lodash.throttle';

import {
  SIZE_SM,
  SIZE_MD,
  SIZE_LG,
  SIZE_XL,
  SIZE_2XL,
  SIZE_3XL,
  BREAKPOINT_MD,
  BREAKPOINT_LG,
  BREAKPOINT_XL,
  BREAKPOINT_2XL,
  BREAKPOINT_3XL,
} from '../utils/screens';

export const BrowserSizeContext = React.createContext<string>(SIZE_LG);

interface BrowserSizeProviderProps {
  children: React.ReactNode;
  userAgent: string;
}

function getBrowserSize(width: number) {
  if (width < BREAKPOINT_MD) {
    return SIZE_SM;
  } else if (width < BREAKPOINT_LG) {
    return SIZE_MD;
  } else if (width < BREAKPOINT_XL) {
    return SIZE_LG;
  } else if (width < BREAKPOINT_2XL) {
    return SIZE_XL;
  } else if (width < BREAKPOINT_3XL) {
    return SIZE_2XL;
  } else {
    return SIZE_3XL;
  }
}

const BrowserSizeProvider: FC<BrowserSizeProviderProps> = React.memo(
  ({ children, userAgent }: BrowserSizeProviderProps) => {
    const [browserSize, setBrowserSize] = useState(
      isMobileUATest(userAgent).any ? SIZE_SM : SIZE_LG
    );

    const handleWindowResize = useCallback(() => {
      const newBrowserSize = getBrowserSize(window.innerWidth);

      if (newBrowserSize !== browserSize) {
        setBrowserSize(newBrowserSize);
      }
    }, [browserSize]);

    useEffect(() => {
      const throttledHandleWindowResize = throttle(handleWindowResize, 50);

      // on first mount, check if window width is still in the same breakpoint as what was detected on the server using the user-agent. if there is a mismatch, update the ui.
      // eg: iPad Pro will be rendered as mobile on server. This will update the ui to desktop on first mount.
      handleWindowResize();

      window.addEventListener('resize', throttledHandleWindowResize);

      return () => window.removeEventListener('resize', throttledHandleWindowResize);
    }, [handleWindowResize]);

    return (
      <BrowserSizeContext.Provider value={browserSize}>{children}</BrowserSizeContext.Provider>
    );
  }
);

BrowserSizeProvider.displayName = 'MobileProvider';

export { BrowserSizeProvider };

function useBrowserSize() {
  const context = React.useContext(BrowserSizeContext);
  return context;
}

export default useBrowserSize;
